/*
 * QQQ - Low-code Application Framework for Engineers.
 * Copyright (C) 2021-2026.  Kingsrook, LLC
 * 651 N Broad St Ste 205 # 6917 | Middletown DE 19709 | United States
 * contact@kingsrook.com
 * https://github.com/Kingsrook/
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <https://www.gnu.org/licenses/>.
 */

import QQueryColumns, {Column} from "qqq/models/query/QQueryColumns";

///////////////////////////////////////////////////////////////////////////////////////
// The compact form of a query screen's columns used in a shared link's "columns"    //
// parameter.  The full QQueryColumns json lists every column, hidden ones included, //
// and for a wide table that alone is longer than a URL can safely be.  This lists   //
// only the visible columns, in display order, comma-separated:                      //
//                                                                                   //
//    NAME[:WIDTH][~L|~R]       e.g.   id~L,clientId:180,orderDate,shipDate~R        //
//                                                                                   //
// WIDTH appears only when it differs from the column's default width, and ~L / ~R   //
// mark a column pinned left / right.  The grid's checkbox column is never listed.   //
///////////////////////////////////////////////////////////////////////////////////////

const CHECKBOX_COLUMN_NAME = "__check__";
const ENTRY_PATTERN = /^([A-Za-z0-9_.]+)(?::(\d+))?(?:~([LR]))?$/;


/*******************************************************************************
 ** build the compact "columns" link value for a set of columns.  defaultColumns
 ** (the table's defaults) decide which widths are worth spelling out.
 *******************************************************************************/
export function encodeQueryColumnsForLink(queryColumns: QQueryColumns, defaultColumns: QQueryColumns): string
{
   const defaultWidthsByName = new Map<string, number>();
   for (const column of defaultColumns.columns)
   {
      defaultWidthsByName.set(column.name, column.width);
   }

   const entries: string[] = [];
   for (const column of queryColumns.columns)
   {
      if (column.name == CHECKBOX_COLUMN_NAME || !column.isVisible)
      {
         continue;
      }

      let entry = column.name;
      if (column.width != null && column.width != defaultWidthsByName.get(column.name))
      {
         entry += `:${Math.round(column.width)}`;
      }
      if (column.pinned == "left")
      {
         entry += "~L";
      }
      else if (column.pinned == "right")
      {
         entry += "~R";
      }
      entries.push(entry);
   }

   return (entries.join(","));
}


/*******************************************************************************
 ** build columns from a compact "columns" link value, on top of the table's
 ** defaults: the listed columns are shown, in the listed order, with their widths
 ** and pins - every other column is hidden, after them, in its default order.
 **
 ** Entries that are malformed, or name a column the table does not have, are
 ** skipped.  Returns null if no entry is usable, so the caller can keep the
 ** columns it already has.
 *******************************************************************************/
export function decodeQueryColumnsFromLink(linkValue: string, defaultColumns: QQueryColumns): QQueryColumns | null
{
   const columnsByName = new Map<string, Column>();
   const checkboxColumns: Column[] = [];
   const otherColumns: Column[] = [];
   for (const defaultColumn of defaultColumns.columns)
   {
      const column: Column = {...defaultColumn};
      if (column.name == CHECKBOX_COLUMN_NAME)
      {
         checkboxColumns.push(column);
         continue;
      }

      column.isVisible = false;
      column.pinned = undefined;
      columnsByName.set(column.name, column);
      otherColumns.push(column);
   }

   const listedColumns: Column[] = [];
   for (const entry of (linkValue ?? "").split(","))
   {
      const match = ENTRY_PATTERN.exec(entry.trim());
      const column = match ? columnsByName.get(match[1]) : null;
      if (!column || listedColumns.includes(column))
      {
         continue;
      }

      if (match[2] != null)
      {
         const width = Number.parseInt(match[2]);
         if (width < 1)
         {
            continue;
         }
         column.width = width;
      }

      column.isVisible = true;
      column.pinned = match[3] == "L" ? "left" : match[3] == "R" ? "right" : undefined;
      listedColumns.push(column);
   }

   if (listedColumns.length == 0)
   {
      return (null);
   }

   const queryColumns = new QQueryColumns();
   queryColumns.columns = [...checkboxColumns, ...listedColumns, ...otherColumns.filter(column => !listedColumns.includes(column))];
   queryColumns.sortColumnsFixingPinPositions();
   return (queryColumns);
}
